/* lib/weather.js — temp/wind/distance conversion + unit-preference hook. */
import { useEffect, useState } from "react";
import { lsGet, lsSet } from "./safeStorage";

const KEY = "habano.weather.unit"; // "f" | "c"
const LANG_KEY = "habano.lang";
const LANG_EVT = "habano:lang-changed";

export function getUnitPref() {
  const v = (lsGet(KEY) || "").toLowerCase();
  return v === "c" ? "c" : "f"; // default °F
}
export function setUnitPref(v) {
  try { lsSet(KEY, v === "c" ? "c" : "f"); } catch { /* ignore */ }
}

/** React hook with localStorage backing + cross-tab sync. */
export function useUnitPref() {
  const [unit, setUnit] = useState(() => getUnitPref());
  useEffect(() => {
    const onStorage = (e) => { if (e.key === KEY) setUnit(getUnitPref()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const toggle = () => {
    const next = unit === "f" ? "c" : "f";
    setUnit(next);
    setUnitPref(next);
  };
  return [unit, toggle];
}

/**
 * iter155 — Language-driven unit hook.
 *
 *   • lang === "en" → "f" (Fahrenheit)
 *   • lang === "es" → "c" (Celsius)
 *
 * Subscribes to the localStorage `habano.lang` value and the sibling
 * `useI18n` context so temperature displays reactively swap unit when
 * the user changes language anywhere in the app.
 *
 * Returns a tuple `[unit]` so callers can drop-in replace the earlier
 * `useUnitPref` (which returned `[unit, toggle]`).
 */
export function useLangUnit() {
  const readUnit = () => ((lsGet(LANG_KEY) || "es").toLowerCase() === "en" ? "f" : "c");
  const [unit, setUnit] = useState(readUnit);
  useEffect(() => {
    const sync = () => setUnit(readUnit());
    const onStorage = (e) => { if (e.key === LANG_KEY) sync(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener(LANG_EVT, sync);
    // Also poll once after mount so an SSR-mismatch or late I18n
    // initialization doesn't leave us stuck on the wrong unit.
    const t = setTimeout(sync, 200);
    return () => {
      clearTimeout(t);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(LANG_EVT, sync);
    };
  }, []);
  return [unit];
}

export function fToC(f) { return (f - 32) * (5 / 9); }
export function cToF(c) { return c * (9 / 5) + 32; }
export function mphToKmh(mph) { return mph * 1.60934; }
export function miToKm(mi) { return mi * 1.60934; }

export function fmtTemp(f, unit) {
  if (f == null || Number.isNaN(f)) return "—";
  const v = unit === "c" ? fToC(f) : f;
  return `${Math.round(v)}°${unit === "c" ? "C" : "F"}`;
}
export function fmtWind(mph, unit) {
  if (mph == null) return "—";
  return unit === "c" ? `${Math.round(mphToKmh(mph))} km/h` : `${Math.round(mph)} mph`;
}
export function fmtDistance(mi, unit) {
  if (mi == null) return "—";
  return unit === "c" ? `${(miToKm(mi)).toFixed(1)} km` : `${mi.toFixed(1)} mi`;
}
export function popPct(pop) { return Math.round((pop || 0) * 100); }

/** Day name for a forecast row's YYYY-MM-DD. */
export function dayLabel(iso, todayIso, lang) {
  if (iso === todayIso) return lang === "es" ? "Hoy" : "Today";
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { weekday: "short" });
  } catch { return iso; }
}

/** Map openweather "main" + icon to a Lucide-compatible icon name. */
export function iconForCondition(main, icon) {
  const m = (main || "").toLowerCase();
  if (m.includes("thunder")) return "CloudLightning";
  if (m.includes("drizzle") || m.includes("rain")) return "CloudRain";
  if (m.includes("snow")) return "CloudSnow";
  if (m.includes("mist") || m.includes("fog") || m.includes("haze")) return "CloudFog";
  if (m.includes("cloud")) return "Cloud";
  // Night clear → Moon
  if ((icon || "").endsWith("n") && m.includes("clear")) return "Moon";
  return "Sun";
}

/** Format ISO timestamp → local HH:MM. */
export function fmtClock(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export function timeAgo(iso) {
  if (!iso) return "";
  try {
    const m = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return ""; }
}


/* iter159 — Translate the OpenWeather English `description` field to
 * Spanish when `lang === "es"`. Falls through to the raw English on
 * unknown terms so the app never renders an empty label.
 *
 * The list covers OpenWeather's 200 -series description phrases
 * seen in Havana + common tourist regions. Anything unmapped simply
 * capitalises the first letter and returns the original text.
 */
const WEATHER_DESC_ES = {
  // Group 8xx — clear / clouds
  "clear sky": "Cielo despejado",
  "few clouds": "Pocas nubes",
  "scattered clouds": "Nubes dispersas",
  "broken clouds": "Nubes fragmentadas",
  "overcast clouds": "Cielo cubierto",
  // Group 5xx — rain
  "light rain": "Lluvia ligera",
  "moderate rain": "Lluvia moderada",
  "heavy intensity rain": "Lluvia intensa",
  "very heavy rain": "Lluvia muy fuerte",
  "extreme rain": "Lluvia extrema",
  "freezing rain": "Lluvia helada",
  "light intensity shower rain": "Chubascos ligeros",
  "shower rain": "Chubascos",
  "heavy intensity shower rain": "Chubascos intensos",
  "ragged shower rain": "Chubascos irregulares",
  // Group 3xx — drizzle
  "light intensity drizzle": "Llovizna ligera",
  "drizzle": "Llovizna",
  "heavy intensity drizzle": "Llovizna intensa",
  // Group 2xx — thunderstorm
  "thunderstorm": "Tormenta eléctrica",
  "thunderstorm with light rain": "Tormenta con lluvia ligera",
  "thunderstorm with rain": "Tormenta con lluvia",
  "thunderstorm with heavy rain": "Tormenta con lluvia intensa",
  "light thunderstorm": "Tormenta ligera",
  "heavy thunderstorm": "Tormenta fuerte",
  // Group 6xx — snow
  "light snow": "Nieve ligera",
  "snow": "Nieve",
  "heavy snow": "Nieve intensa",
  // Group 7xx — atmosphere
  "mist": "Neblina",
  "fog": "Niebla",
  "haze": "Bruma",
  "smoke": "Humo",
  "dust": "Polvo",
  "sand": "Arena",
  "ash": "Ceniza volcánica",
  "squalls": "Ráfagas",
  "tornado": "Tornado",
};

export function translateWeatherDesc(text, lang) {
  if (!text) return "";
  const raw = String(text).trim();
  if (!raw) return "";
  if (lang !== "es") return raw;
  const key = raw.toLowerCase();
  const es = WEATHER_DESC_ES[key];
  if (es) return es;
  // Unknown phrase → return raw text unchanged (still readable in EN).
  return raw;
}
